import { Parameter } from '@shared/types';

/**
 * Safe, sandboxed visibility expression evaluator.
 *
 * Supported grammar:
 *   expr       ::= or_expr
 *   or_expr    ::= and_expr ( "||" and_expr )*
 *   and_expr   ::= not_expr ( "&&" not_expr )*
 *   not_expr   ::= "!" not_expr | comparison
 *   comparison ::= atom ( ("==" | "!=" | "<" | ">" | "<=" | ">=") atom )?
 *   atom       ::= "true" | "false" | NUMBER | STRING | IDENTIFIER | "(" expr ")"
 *
 * Security guarantees:
 * - No eval(), Function(), or any dynamic code execution
 * - No property access chains (foo.bar) — single identifiers only
 * - Result is always boolean — never rendered as HTML
 * - Hard token limit prevents pathological inputs
 * - All errors default to `true` (show the parameter)
 *
 * Circular dependency note: expressions are read-only; they read a snapshot of
 * current values and produce a boolean. No values are mutated, so no cycles
 * are possible.
 */

// ---------------------------------------------------------------------------
// Tokeniser
// ---------------------------------------------------------------------------

type TokenKind =
  | 'NUMBER'
  | 'STRING'
  | 'IDENT'
  | 'TRUE'
  | 'FALSE'
  | 'EQ' // ==
  | 'NEQ' // !=
  | 'LT' // <
  | 'GT' // >
  | 'LTE' // <=
  | 'GTE' // >=
  | 'AND' // &&
  | 'OR' // ||
  | 'NOT' // !
  | 'LPAREN'
  | 'RPAREN'
  | 'EOF';

interface Token {
  kind: TokenKind;
  value: string | number | boolean;
}

const MAX_TOKENS = 200;

function tokenise(src: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < src.length) {
    // Skip whitespace
    if (/\s/.test(src[i])) {
      i++;
      continue;
    }

    // Two-char operators first
    if (src[i] === '=' && src[i + 1] === '=') {
      tokens.push({ kind: 'EQ', value: '==' });
      i += 2;
      continue;
    }
    if (src[i] === '!' && src[i + 1] === '=') {
      tokens.push({ kind: 'NEQ', value: '!=' });
      i += 2;
      continue;
    }
    if (src[i] === '<' && src[i + 1] === '=') {
      tokens.push({ kind: 'LTE', value: '<=' });
      i += 2;
      continue;
    }
    if (src[i] === '>' && src[i + 1] === '=') {
      tokens.push({ kind: 'GTE', value: '>=' });
      i += 2;
      continue;
    }
    if (src[i] === '&' && src[i + 1] === '&') {
      tokens.push({ kind: 'AND', value: '&&' });
      i += 2;
      continue;
    }
    if (src[i] === '|' && src[i + 1] === '|') {
      tokens.push({ kind: 'OR', value: '||' });
      i += 2;
      continue;
    }

    // Single-char operators
    if (src[i] === '<') {
      tokens.push({ kind: 'LT', value: '<' });
      i++;
      continue;
    }
    if (src[i] === '>') {
      tokens.push({ kind: 'GT', value: '>' });
      i++;
      continue;
    }
    if (src[i] === '!') {
      tokens.push({ kind: 'NOT', value: '!' });
      i++;
      continue;
    }
    if (src[i] === '(') {
      tokens.push({ kind: 'LPAREN', value: '(' });
      i++;
      continue;
    }
    if (src[i] === ')') {
      tokens.push({ kind: 'RPAREN', value: ')' });
      i++;
      continue;
    }

    // Numbers (integer or decimal, optionally negative via unary minus handled in parser)
    if (/[\d.]/.test(src[i])) {
      let num = '';
      while (i < src.length && /[\d.]/.test(src[i])) {
        num += src[i++];
      }
      const parsed = Number(num);
      if (isNaN(parsed)) throw new Error(`Invalid number: ${num}`);
      tokens.push({ kind: 'NUMBER', value: parsed });
      continue;
    }

    // Strings — single or double quoted, no escape sequences needed
    if (src[i] === '"' || src[i] === "'") {
      const quote = src[i++];
      let str = '';
      while (i < src.length && src[i] !== quote) {
        // Only allow printable ASCII inside strings to prevent injection
        const ch = src[i++];
        if (ch.charCodeAt(0) < 0x20)
          throw new Error('Control character in string');
        str += ch;
      }
      if (src[i] !== quote) throw new Error('Unterminated string');
      i++; // consume closing quote
      tokens.push({ kind: 'STRING', value: str });
      continue;
    }

    // Identifiers / keywords
    if (/[a-zA-Z_$]/.test(src[i])) {
      let id = '';
      while (i < src.length && /[a-zA-Z0-9_$]/.test(src[i])) {
        id += src[i++];
      }
      if (id === 'true') {
        tokens.push({ kind: 'TRUE', value: true });
        continue;
      }
      if (id === 'false') {
        tokens.push({ kind: 'FALSE', value: false });
        continue;
      }
      tokens.push({ kind: 'IDENT', value: id });
      continue;
    }

    // Unrecognised character
    throw new Error(`Unexpected character: ${src[i]}`);
  }

  if (tokens.length > MAX_TOKENS) {
    throw new Error('Expression too long');
  }

  tokens.push({ kind: 'EOF', value: '' });
  return tokens;
}

// ---------------------------------------------------------------------------
// Parser / evaluator (recursive descent)
// ---------------------------------------------------------------------------

type ValueMap = Record<string, Parameter['value']>;

class Parser {
  private tokens: Token[];
  private pos = 0;

  constructor(tokens: Token[]) {
    this.tokens = tokens;
  }

  private peek(): Token {
    return this.tokens[this.pos];
  }
  private consume(): Token {
    return this.tokens[this.pos++];
  }

  private expect(kind: TokenKind): Token {
    const tok = this.consume();
    if (tok.kind !== kind) throw new Error(`Expected ${kind}, got ${tok.kind}`);
    return tok;
  }

  parse(vars: ValueMap): boolean {
    const result = this.parseOr(vars);
    if (this.peek().kind !== 'EOF')
      throw new Error('Unexpected token after expression');
    return Boolean(result);
  }

  private parseOr(vars: ValueMap): unknown {
    let left = this.parseAnd(vars);
    while (this.peek().kind === 'OR') {
      this.consume();
      const right = this.parseAnd(vars);
      left = Boolean(left) || Boolean(right);
    }
    return left;
  }

  private parseAnd(vars: ValueMap): unknown {
    let left = this.parseNot(vars);
    while (this.peek().kind === 'AND') {
      this.consume();
      const right = this.parseNot(vars);
      left = Boolean(left) && Boolean(right);
    }
    return left;
  }

  private parseNot(vars: ValueMap): unknown {
    if (this.peek().kind === 'NOT') {
      this.consume();
      return !Boolean(this.parseNot(vars));
    }
    return this.parseComparison(vars);
  }

  private parseComparison(vars: ValueMap): unknown {
    const left = this.parseAtom(vars);
    const op = this.peek().kind;

    if (
      op === 'EQ' ||
      op === 'NEQ' ||
      op === 'LT' ||
      op === 'GT' ||
      op === 'LTE' ||
      op === 'GTE'
    ) {
      this.consume();
      const right = this.parseAtom(vars);

      switch (op) {
        case 'EQ':
          return left === right;
        case 'NEQ':
          return left !== right;
        case 'LT':
          return (left as number) < (right as number);
        case 'GT':
          return (left as number) > (right as number);
        case 'LTE':
          return (left as number) <= (right as number);
        case 'GTE':
          return (left as number) >= (right as number);
      }
    }

    return left;
  }

  private parseAtom(vars: ValueMap): unknown {
    const tok = this.peek();

    if (tok.kind === 'TRUE') {
      this.consume();
      return true;
    }
    if (tok.kind === 'FALSE') {
      this.consume();
      return false;
    }
    if (tok.kind === 'NUMBER') {
      this.consume();
      return tok.value;
    }
    if (tok.kind === 'STRING') {
      this.consume();
      return tok.value;
    }

    if (tok.kind === 'IDENT') {
      this.consume();
      const name = tok.value as string;
      const val = vars[name];
      if (val === undefined) return undefined;
      // For arrays/objects return undefined (not comparable with primitives)
      if (typeof val === 'object') return undefined;
      return val;
    }

    if (tok.kind === 'LPAREN') {
      this.consume();
      const inner = this.parseOr(vars);
      this.expect('RPAREN');
      return inner;
    }

    throw new Error(`Unexpected token: ${tok.kind}`);
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Build a flat value map from an array of parameters for use in evaluateVisibility.
 */
export function buildValueMap(
  parameters: Parameter[],
): Record<string, Parameter['value']> {
  const map: Record<string, Parameter['value']> = {};
  for (const p of parameters) {
    map[p.name] = p.value;
  }
  return map;
}

/**
 * Evaluate a visibility expression string against the given parameter value map.
 *
 * Returns `true` if:
 * - expr is undefined, null, or empty (no guard → always visible)
 * - the expression evaluates to a truthy value
 * - the expression is syntactically or semantically invalid (fail-safe: show)
 *
 * Returns `false` only when the expression explicitly evaluates to false.
 */
export function evaluateVisibility(
  expr: string | undefined,
  vars: Record<string, Parameter['value']>,
): boolean {
  if (!expr || expr.trim() === '') return true;

  try {
    const tokens = tokenise(expr.trim());
    const parser = new Parser(tokens);
    return parser.parse(vars);
  } catch {
    // Any error (parse, runtime, etc.) → show by default
    return true;
  }
}
